import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { generateHrefLangLinks, getLanguageFromPath } from '../utils/i18nRouter';
import { getToolById, getCategoryById } from '../data/tools';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = '/og-image.png',
  type = 'website',
  noindex = false,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // 解析当前路径
  const currentLang = getLanguageFromPath(location.pathname) || 'en';
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // 移除语言前缀
  const cleanSegments = currentLang !== 'en' && pathSegments[0] === currentLang
    ? pathSegments.slice(1)
    : pathSegments;

  // 生成动态SEO数据
  const generateSEOData = () => {
    const baseUrl = 'https://toolkitclub.site';
    const siteTitle = t('header.title');
    const siteDescription = t('header.description');

    // 默认SEO数据
    let seoData = {
      title: title || siteTitle,
      description: description || siteDescription,
      keywords: keywords || 'online tools, developer tools, json formatter, base64 encoder, color converter, timestamp converter, uuid generator, markdown editor, free tools',
      image: image,
      url: `${baseUrl}${location.pathname}`
    };

    // 根据路径类型生成特定SEO数据
    if (cleanSegments.length === 0) {
      // 首页
      seoData = {
        title: `${siteTitle} - ${t('header.subtitle')}`,
        description: t('seo.homepage.description') || 'Access 100+ free online tools for developers, content creators, and everyday tasks. No downloads required - use directly in your browser!',
        keywords: 'online tools, free tools, developer tools, web tools, utilities, json formatter, base64 encoder, color converter, qr generator, password generator',
        image: `${baseUrl}/og-homepage.png`,
        url: `${baseUrl}${location.pathname}`
      };
    } else if (cleanSegments[0] === 'tools' && cleanSegments.length === 3) {
      // 工具页面: /tools/category/tool-id
      const [, categoryId, toolId] = cleanSegments;
      const tool = getToolById(toolId);
      const category = getCategoryById(categoryId);

      if (tool && category) {
        seoData = {
          title: `${t(tool.name)} - Free Online Tool | ${siteTitle}`,
          description: `${t(tool.description)} Use our free ${t(tool.name).toLowerCase()} tool online. No downloads required, works in your browser.`,
          keywords: `${t(tool.keywords)}, ${t(`categories.${categoryId}`)}, online tool, free tool, web tool`,
          image: `${baseUrl}/og-tool-${toolId}.png`,
          url: `${baseUrl}${location.pathname}`
        };
      }
    } else if (cleanSegments[0] === 'privacy') {
      // 隐私政策页面
      seoData = {
        title: `Privacy Policy | ${siteTitle}`,
        description: 'Learn about how ToolKit Club protects your privacy and handles your data. We are committed to transparency and user privacy.',
        keywords: 'privacy policy, data protection, user privacy, toolkitclub privacy',
        image: `${baseUrl}/og-privacy.png`,
        url: `${baseUrl}${location.pathname}`
      };
    } else if (cleanSegments[0] === 'terms') {
      // 服务条款页面
      seoData = {
        title: `Terms of Service | ${siteTitle}`,
        description: 'Read the terms of service for ToolKit Club. Understand your rights and responsibilities when using our free online tools.',
        keywords: 'terms of service, user agreement, toolkitclub terms, service terms',
        image: `${baseUrl}/og-terms.png`,
        url: `${baseUrl}${location.pathname}`
      };
    }

    return seoData;
  };

  const seoData = generateSEOData();
  const pageTitle = seoData.title;
  const pageDescription = seoData.description;
  const pageKeywords = seoData.keywords;
  const currentUrl = seoData.url;
  const siteTitle = t('header.title');

  const hrefLangLinks = generateHrefLangLinks(location.pathname);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": siteTitle,
    "description": pageDescription,
    "url": currentUrl,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "ToolkitClub"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="ToolkitClub" />
      <meta name="language" content={i18n.language} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={seoData.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={i18n.language} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@toolkitclub" />
      <meta name="twitter:creator" content="@toolkitclub" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={seoData.image} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Alternate Languages */}
      {hrefLangLinks.map(({ hrefLang, href }) => (
        <link
          key={hrefLang}
          rel="alternate"
          hrefLang={hrefLang}
          href={`https://toolkitclub.site${href}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`https://toolkitclub.site${hrefLangLinks.find(link => link.hrefLang === 'en')?.href || '/'}`} />
    </Helmet>
  );
};
